<template>
  <b-modal
    id="bau-complement-wizard"
    size="lg"
    :title="isPrefillMode ? $t('bau_complement.start_form') : $t('bau_complement.new')"
    no-close-on-backdrop
    hide-footer
    @show="onModalShow"
    @hide="onModalHide"
  >
    <validation-observer ref="validationObserver">
      <b-form @submit.prevent>
        <b-progress :value="(currentStep / totalSteps) * 100" height="2px" class="mb-2" />

        <div v-show="currentStep === 1">
          <h5 class="mb-2 wizard-title">
            {{ $t("unit.single") }}<span class="text-danger">*</span>
          </h5>
          <v-select
            v-model="selectedUnit"
            :options="unitOptions"
            :get-option-label="getUnitLabel"
            :reduce="(option) => option"
            :placeholder="$t('unit.single')"
            :clearable="true"
            :loading="isLoadingUnits"
            :filterable="false"
            @search="onUnitSearch"
            class="mb-2"
            :class="{ 'is-invalid': showError }"
          />
          <div class="d-flex flex-column flex-md-row">
            <div class="mr-md-2 mb-2 mb-md-0" style="flex:1;">
              <h5 class="mb-1 wizard-title">REMS</h5>
              <b-form-input v-model.number="selectedRems" type="number" disabled />
            </div>
            <div style="flex:1;">
              <h5 class="mb-1 wizard-title">SIRH</h5>
              <b-form-input v-model.number="selectedSirh" type="number" disabled />
            </div>
          </div>
          <small v-if="showError" class="text-danger">
            {{ $t("validation.required", { field: $t("unit.single") }) }}
          </small>
        </div>

        <div v-show="currentStep === 2">
          <bau-complement-assessment-step
            v-if="selectedUnit"
            :key="
              (selectedUnit && selectedUnit.id) ||
              (selectedUnitData && selectedUnitData.id_tenant) ||
              'no-unit'
            "
            :prop-item-data="{ ...selectedUnitData, reference_number: referenceNumber }"
            :existing-bau-complement-id="isPrefillMode ? prefill.bauComplementId : null"
            :existing-record-last-status-name="isPrefillMode ? prefill.existingRecordLastStatusName : null"
            @open-bau-complement-modal="handleOpen"
          />
        </div>

        <div class="d-flex justify-content-between mt-2">
          <b-button
            type="button"
            v-if="currentStep > 1 && !isPrefillMode"
            variant="outline-secondary"
            @click="currentStep--"
          >
            {{ $t("actions.previous") }}
          </b-button>
          <b-button
            type="button"
            v-if="currentStep < totalSteps"
            variant="primary"
            @click="nextStep"
            :disabled="!isConsistent"
          >
            {{ $t("actions.next") }}
          </b-button>
        </div>
      </b-form>
    </validation-observer>
  </b-modal>
</template>

<script>
import { ref, computed, watch } from "@vue/composition-api";
import { ValidationObserver } from "vee-validate";
import {
  BModal,
  BForm,
  BFormInput,
  BButton,
  BProgress,
} from "bootstrap-vue";
import { useMainUnitWizardOptions } from "@/libs/risk/useMainUnitWizardOptions";
import vSelect from "vue-select";
import axios from "@/libs/axios";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import BauComplementAssessmentStep from "./BauComplementAssessmentStep.vue";

export default {
  name: "BauComplementWizard",
  components: {
    ValidationObserver,
    BModal,
    BForm,
    BFormInput,
    BButton,
    BProgress,
    vSelect,
    BauComplementAssessmentStep,
  },
  props: {
    prefill: {
      type: Object,
      default: null,
    },
  },
  setup(props, { emit, root }) {
    const toast = useToast();
    const currentStep = ref(1);
    const totalSteps = 2;
    const selectedUnit = ref(null);
    const selectedRems = ref(null);
    const selectedSirh = ref(null);
    const validationObserver = ref(null);
    const showError = ref(false);

    const generateReferenceNumber = () => {
      const year = new Date().getFullYear().toString().slice(-2);
      return `BAU-${year}`;
    };

    const referenceNumber = ref(generateReferenceNumber());
    const userData = JSON.parse(localStorage.getItem("userData"));

    const { isLoadingUnits, unitOptions, fetchMainUnits, onUnitSearch, resolveUnitById } =
      useMainUnitWizardOptions(userData, { mainUnitOnly: false });

    // Carga inicial (server-side, una página); las siguientes vienen del @search
    fetchMainUnits();

    const getUnitLabel = (option) => {
      if (!option) return "";
      const remsText =
        option.rems === null || option.rems === undefined || option.rems === ""
          ? "No REMS"
          : `REMS: ${option.rems}`;
      const sirhText =
        option.sirh === null || option.sirh === undefined || option.sirh === ""
          ? "No SIRH"
          : `SIRH: ${option.sirh}`;
      return `${option.description} (${remsText}, ${sirhText})`;
    };

    const isConsistent = computed(() => {
      if (!selectedUnit.value) return false;
      const unitRems = selectedUnit.value.rems;
      const unitSirh = selectedUnit.value.sirh;
      const inputRems = selectedRems.value;
      const inputSirh = selectedSirh.value;
      const remsMatches =
        unitRems === null || unitRems === undefined || unitRems === ""
          ? inputRems === null || inputRems === undefined || inputRems === ""
          : Number(unitRems) === Number(inputRems);
      const sirhMatches =
        unitSirh === null || unitSirh === undefined || unitSirh === ""
          ? inputSirh === null || inputSirh === undefined || inputSirh === ""
          : Number(unitSirh) === Number(inputSirh);
      return remsMatches && sirhMatches;
    });

    const selectedUnitData = computed(() => {
      if (!selectedUnit.value) return null;
      const unitTenant = selectedUnit.value.router?.id_tenant || null;
      return { id: selectedUnit.value.id, id_tenant: unitTenant };
    });

    const isPrefillMode = computed(() => {
      const prefill = props.prefill;
      return !!(prefill && prefill.bauComplementId != null && prefill.unitId != null);
    });

    const nextStep = async () => {
      if (currentStep.value === 1 && !selectedUnit.value) {
        showError.value = true;
        return;
      }
      showError.value = false;
      if (currentStep.value === 1) {
        const exists = await checkActiveBauComplement(selectedUnit.value.id);
        if (exists) return;
      }

      if (currentStep.value < totalSteps) currentStep.value += 1;
    };

    const checkActiveBauComplement = async (unitId) => {
      try {
        const { data } = await axios.get("/v1/bau-complements", {
          params: { id_unit: unitId, with: ["last_status"] },
        });
        const exists = !!data?.exists;
        if (exists) {
          const reference = data?.[0]?.reference_number || "";
          const title = reference
            ? `Ya existe un complemento BAU en progreso (Ref: ${reference})`
            : "Ya existe un complemento BAU en progreso para esta instalación";
          toast({
            component: ToastificationContent,
            props: {
              title,
              icon: "AlertTriangleIcon",
              variant: "warning",
            },
          });
          return true;
        }
        return false;
      } catch (_) {
        return false;
      }
    };

    const handleOpen = ({ id, snapshot, sources }) => {
      root.$bvModal.hide("bau-complement-wizard");
      emit("open-bau-complement-modal", { id, snapshot, sources });
    };

    const resetWizardState = () => {
      currentStep.value = 1;
      selectedUnit.value = null;
      selectedRems.value = null;
      selectedSirh.value = null;
      showError.value = false;
      referenceNumber.value = generateReferenceNumber();
      validationObserver.value?.reset();
    };

    const onModalShow = async () => {
      resetWizardState();
      const prefill = props.prefill;
      if (!prefill || prefill.bauComplementId == null || prefill.unitId == null) return;
      const unit = await resolveUnitById(Number(prefill.unitId));
      if (!unit) return;
      referenceNumber.value = prefill.referenceNumber || generateReferenceNumber();
      selectedUnit.value = unit;
      currentStep.value = 2;
    };

    const onModalHide = () => {
      resetWizardState();
      emit("wizard-closed");
    };

    watch(selectedUnit, (unit) => {
      if (!unit) {
        selectedRems.value = null;
        selectedSirh.value = null;
        return;
      }
      selectedRems.value =
        unit.rems !== null && unit.rems !== undefined && unit.rems !== ""
          ? Number(unit.rems)
          : null;
      selectedSirh.value =
        unit.sirh !== null && unit.sirh !== undefined && unit.sirh !== ""
          ? Number(unit.sirh)
          : null;
    });

    return {
      currentStep,
      totalSteps,
      selectedUnit,
      unitOptions,
      referenceNumber,
      selectedUnitData,
      nextStep,
      onModalShow,
      onModalHide,
      validationObserver,
      showError,
      selectedRems,
      selectedSirh,
      getUnitLabel,
      isConsistent,
      handleOpen,
      isPrefillMode,
      isLoadingUnits,
      onUnitSearch,
    };
  },
};
</script>
